/* release-features: scroll-spy for the feature rows and the app clips in their phones (sections/release-features.liquid).
   - Active row: the row whose top last passed a reading line at 40 % of the viewport. An IntersectionObserver on that
     line and a rAF-throttled scroll listener trigger the check; positions are cached. The rail link gets aria-current.
   - Rail (from 1100 px): the fill follows the reading line between the row tops; if the list is taller than
     its window, the list slides so the active stop stays in view (also for keyboard focus).
   - Bar (below 1100 px): "03 / 10 · bank select" under the header while the rows are on screen.
   Measurements are cached and refreshed on resize; the scroll handler only reads the cache (one rAF per frame).
   - Clips (snippets/release-video.liquid, video[data-rf-clip]): muted loops with preload="none". Within half a screen
     of the viewport a clip may start to load (preload="metadata"); it plays while at least a quarter of it is on screen
     and pauses once it is off screen. Reduced motion (also when switched on later) and Save-Data keep the poster until
     the visitor presses play. A refused play() (e.g. iOS Low Power Mode) keeps the poster and waits for a press.
     The button (rendered hidden under the device, shown here; it and its video share a [data-rf-clip-box]) is named
     after what a press does: data-label-pause / data-label-play.
     Without an original in the markup (data-hls), a WebKit phone gets HLS put in first on the first play, so it skips
     the metadata preload: play() reloads anyway, and the mp4 metadata would be a second clip file.
     A press wins over the automatic rules: paused by hand stays paused, started by hand keeps playing in view.
   Fault isolated: any error leaves the page as plain anchors and still screenshots. */
(function () {
  'use strict';
  if (window.__afkReleaseFeatures) return;
  window.__afkReleaseFeatures = true;
  var LINE = 0.4;

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function init(root) {
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-rf-row]'));
    if (!rows.length || !('IntersectionObserver' in window)) return;
    var rail = root.querySelector('[data-rf-rail]');
    var links = rail ? Array.prototype.slice.call(rail.querySelectorAll('[data-rf-link]')) : [];
    var view = rail && rail.querySelector('[data-rf-view]');
    var inner = rail && rail.querySelector('[data-rf-inner]');
    var track = rail && rail.querySelector('[data-rf-track]');
    var fill = rail && rail.querySelector('[data-rf-fill]');
    var rowsWrap = root.querySelector('[data-rf-rows]');
    var bar = root.querySelector('[data-rf-bar]');
    var barNum = bar && bar.querySelector('[data-rf-bar-num]');
    var barLabel = bar && bar.querySelector('[data-rf-bar-label]');
    var barProgress = bar && bar.querySelector('[data-rf-bar-progress]');
    if (!rail && !bar) return; /* script loaded for clips only */
    var header = document.querySelector('.site-header');
    var labels = rows.map(function (r, i) {
      var l = links[i] && links[i].querySelector('.rf-rail__label');
      var t = r.querySelector('.rf-row__tag');
      return (l || t) ? (l || t).textContent.trim() : '';
    });
    var total = rows.length;
    var active = -1;
    var tops = [], centers = [], lastBottom = 0, railOn = false, windowed = false, shift = 0, ticking = false;
    var lineIO = null, barIO = null;

    function setActive(i) {
      if (i === active || i < 0 || i >= total) return;
      active = i;
      links.forEach(function (a, k) {
        if (k === i) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
        a.classList.toggle('is-passed', k < i);
      });
      if (barNum) barNum.textContent = pad(i + 1) + ' / ' + pad(total);
      if (barLabel) barLabel.textContent = labels[i];
      if (barProgress) barProgress.style.transform = 'scaleX(' + ((i + 1) / total) + ')';
      keepInView(links[i]);
    }

    /* list window: a whole number of stops (n); slide by whole stops so the given link sits in the middle */
    var perView = total;
    function keepInView(link) {
      if (!railOn || !windowed || !link) return;
      var k = links.indexOf(link);
      var first = Math.max(0, Math.min(total - perView, k - Math.floor(perView / 2)));
      var next = links[first].offsetTop - links[0].offsetTop;
      if (Math.abs(next - shift) < 1) return;
      shift = next;
      inner.style.transform = 'translateY(' + (-shift) + 'px)';
    }

    function headerBottom() {
      return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    }

    function measure() {
      var hb = headerBottom();
      root.style.setProperty('--rf-hdr', hb + 'px');
      if (bar) root.style.setProperty('--rf-bar-top', hb + 'px');
      var y = window.scrollY || window.pageYOffset;
      tops = rows.map(function (r) { return r.getBoundingClientRect().top + y; });
      var last = rows[total - 1].getBoundingClientRect();
      lastBottom = last.bottom + y;
      railOn = !!(rail && rail.offsetParent !== null);
      if (railOn) {
        centers = links.map(function (a) { return a.offsetTop + a.offsetHeight / 2; });
        if (track && centers.length > 1) {
          track.style.top = centers[0] + 'px';
          track.style.bottom = (inner.scrollHeight - centers[centers.length - 1]) + 'px';
        }
        view.style.maxHeight = '';
        windowed = inner.scrollHeight > view.clientHeight + 1;
        if (windowed) {
          var itemH = links[0].offsetHeight || 34;
          perView = Math.max(3, Math.floor(view.clientHeight / itemH));
          view.style.maxHeight = (perView * itemH) + 'px';
        } else {
          perView = total;
          if (shift) { shift = 0; inner.style.transform = ''; }
        }
        keepInView(links[active]);
        root.style.setProperty('--rf-rail-h', rail.offsetHeight + 'px');
      }
      observe(hb);
      update();
    }

    /* active row = the last row whose top has passed the reading line (also after a jump that lands between rows);
       fill = piecewise linear between the row tops, so it meets each stop when that row becomes active */
    function update() {
      ticking = false;
      if (!tops.length) return;
      var y = (window.scrollY || window.pageYOffset) + window.innerHeight * LINE;
      var i = 0;
      while (i < total - 1 && y >= tops[i + 1]) i++;
      setActive(i);
      if (!railOn || !fill || centers.length < 2) return;
      var span = centers[centers.length - 1] - centers[0];
      var len = 0;
      if (y >= tops[0]) {
        if (i >= total - 1) {
          len = span;
        } else {
          var f = (y - tops[i]) / Math.max(1, tops[i + 1] - tops[i]);
          len = centers[i] - centers[0] + f * (centers[i + 1] - centers[i]);
        }
      }
      fill.style.transform = 'scaleY(' + Math.max(0, Math.min(1, len / Math.max(1, span))) + ')';
    }

    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }

    function observe(hb) {
      if (lineIO) lineIO.disconnect();
      var vh = window.innerHeight;
      var topM = Math.round(vh * LINE);
      lineIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) onScroll();
        });
      }, { rootMargin: '-' + topM + 'px 0px -' + (vh - topM - 1) + 'px 0px', threshold: 0 });
      rows.forEach(function (r) { lineIO.observe(r); });

      if (bar && rowsWrap) {
        if (barIO) barIO.disconnect();
        /* visible while the rows cover the strip just under the header */
        barIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            bar.classList.toggle('is-visible', en.isIntersecting);
            bar.inert = !en.isIntersecting;
          });
        }, { rootMargin: '-' + (hb + 44) + 'px 0px -' + Math.max(0, vh - hb - 46) + 'px 0px', threshold: 0 });
        barIO.observe(rowsWrap);
      }
    }

    links.forEach(function (a, i) {
      a.addEventListener('click', function () { setActive(i); });
      a.addEventListener('focus', function () { keepInView(a); });
    });

    setActive(0);
    if (bar) bar.inert = true;
    measure();
    var rt;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(measure, 120); }, { passive: true });
    window.addEventListener('load', measure);
    window.addEventListener('scroll', onScroll, { passive: true });
    if ('ResizeObserver' in window && rowsWrap) {
      var ro = new ResizeObserver(function () { clearTimeout(rt); rt = setTimeout(measure, 120); });
      ro.observe(rowsWrap);
    }
  }

  /* ---------- app clips ---------- */
  var PLAY_AT = 0.25;
  var motionMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var phoneMQ = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var webkitHls = /Apple/.test(navigator.vendor || '');
  var clips = [];
  var viewIO = null, nearIO = null;

  function still() { return saveData || !!(motionMQ && motionMQ.matches); }
  /* may the clip run on its own? a press wins over reduced motion and Save-Data */
  function mayRun(c) { return c.user ? c.user === 'play' : !c.refused && !still(); }

  function sync(c) {
    var paused = c.v.paused;
    c.b.setAttribute('aria-label', paused ? c.playLabel : c.pauseLabel);
    c.b.setAttribute('data-state', paused ? 'paused' : 'playing');
  }

  function play(c) {
    var v = c.v;
    var hls = v.getAttribute('data-hls');
    if (hls) {
      /* no original found in the markup: HLS first on WebKit phones, as snippets/lazy-video does */
      v.removeAttribute('data-hls');
      if (webkitHls && phoneMQ && phoneMQ.matches) {
        var s = document.createElement('source');
        s.src = hls; s.type = 'application/x-mpegURL';
        v.insertBefore(s, v.firstChild);
        v.load();
      }
    }
    var p = v.play();
    /* AbortError only means pause() or load() came first; any other refusal waits for a press */
    if (p && p.catch) p.catch(function (e) {
      if (!e || e.name !== 'AbortError') c.refused = true;
      sync(c);
    });
  }

  function press(c) {
    if (c.v.paused) { c.user = 'play'; c.refused = false; play(c); }
    else { c.user = 'pause'; c.v.pause(); }
  }

  function onView(entries) {
    entries.forEach(function (en) {
      var c = en.target._rfClip;
      if (!c) return;
      if (!en.isIntersecting) {
        c.inView = false;
        if (!c.v.paused) c.v.pause();
      } else if (en.intersectionRatio >= PLAY_AT - 0.01) {
        c.inView = true;
        if (c.v.paused && mayRun(c)) play(c);
      }
    });
  }

  function onNear(entries) {
    entries.forEach(function (en) {
      var c = en.target._rfClip;
      if (!c || !en.isIntersecting) return;
      nearIO.unobserve(c.v);
      if (!mayRun(c) || c.v.preload !== 'none') return;
      if (c.v.hasAttribute('data-hls') && webkitHls && phoneMQ && phoneMQ.matches) return;
      c.v.preload = 'metadata';
    });
  }

  function onMotion() {
    clips.forEach(function (c) {
      if (mayRun(c)) { if (c.inView && c.v.paused) play(c); }
      else if (!c.v.paused) c.v.pause();
    });
  }

  function initClips(scope) {
    var vids = (scope || document).querySelectorAll('video[data-rf-clip]');
    Array.prototype.forEach.call(vids, function (v) {
      if (v._rfClip) return;
      var box = (v.closest && v.closest('[data-rf-clip-box]')) || v.parentNode;
      var b = box && box.querySelector('[data-rf-clip-toggle]');
      if (!b) return;
      var c = { v: v, b: b, user: '', refused: false, inView: false,
        pauseLabel: b.getAttribute('data-label-pause') || 'pause', playLabel: b.getAttribute('data-label-play') || 'play' };
      v._rfClip = c;
      v.muted = true; /* the property, not only the attribute: autoplay rules read it */
      v.addEventListener('play', function () { sync(c); });
      v.addEventListener('pause', function () { sync(c); });
      b.addEventListener('click', function () { press(c); });
      sync(c);
      b.hidden = false;
      clips.push(c);
      if (!('IntersectionObserver' in window)) { if (mayRun(c)) play(c); return; }
      if (!viewIO) {
        viewIO = new IntersectionObserver(onView, { threshold: [0, PLAY_AT] });
        nearIO = new IntersectionObserver(onNear, { rootMargin: '50% 0px' });
      }
      viewIO.observe(v);
      nearIO.observe(v);
    });
  }

  function dropClips(scope) {
    clips = clips.filter(function (c) {
      if (!scope.contains(c.v)) return true;
      if (viewIO) { viewIO.unobserve(c.v); nearIO.unobserve(c.v); }
      c.v.pause();
      return false;
    });
  }

  function boot() {
    document.querySelectorAll('[data-rf]').forEach(function (root) {
      try { init(root); } catch (e) { if (window.console) console.warn('release-features', e); }
    });
    try { initClips(document); } catch (e) { if (window.console) console.warn('release-features clips', e); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  if (motionMQ) {
    if (motionMQ.addEventListener) motionMQ.addEventListener('change', onMotion);
    else if (motionMQ.addListener) motionMQ.addListener(onMotion);
  }
  /* theme editor: clips of a re-rendered section */
  document.addEventListener('shopify:section:load', function (e) {
    try { initClips(e.target); } catch (err) { if (window.console) console.warn('release-features clips', err); }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    try { dropClips(e.target); } catch (err) { /* nothing to undo */ }
  });
})();
